import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  Toolbar,
  SaveButton,
  useTranslate,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const FuelToolbar = ({ isEdit, onDelete }: { isEdit?: boolean; onDelete?: () => void }) => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <Box>
        {isEdit && onDelete && (
          <Button onClick={onDelete} color="error" variant="outlined" sx={{ mr: 1 }}>
            {translate('ra.action.delete')}
          </Button>
        )}
      </Box>
      <SaveButton label={translate('ra.action.save')} />
    </Toolbar>
  );
};

interface FuelDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const FuelDialog: React.FC<FuelDialogProps> = ({
  open,
  onClose,
  equipmentId,
  recordId,
  isEdit = false,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [record, setRecord] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const fuelTypes = [
    { id: 'GASOLINE', name: 'Gasoline' },
    { id: 'DIESEL', name: 'Diesel' },
    { id: 'PROPANE', name: 'Propane' },
    { id: 'ELECTRIC', name: 'Electric' },
    { id: 'HYBRID', name: 'Hybrid' },
  ];

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      true;
      dataProvider
        .getOne('equipment-fuel', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch(error => {
          console.error('Error loading fuel record:', error);
          notify('Error loading fuel record', { type: 'error' });
        })
        .finally(() => {
          false;
        });
    } else if (!isEdit) {
      setRecord(null);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    true;
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };

      if (isEdit && recordId) {
        await dataProvider.update('equipment-fuel', {
          id: recordId,
          data: formattedData,
        });
        notify('Fuel record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-fuel', {
          data: formattedData,
        });
        notify('Fuel record created successfully', { type: 'success' });
      }

      refresh();
      onClose();
    } catch (error) {
      console.error('Fuel submission error:', error);
      notify(
        `Error ${isEdit ? 'updating' : 'creating'} fuel record: ${
          error.message || 'Unknown error'
        }`,
        { type: 'error' }
      );
    } finally {
      false;
    }
  };

  const handleDelete = async () => {
    if (!recordId) {
      return;
    }

    true;
    try {
      await dataProvider.delete('equipment-fuel', { id: recordId });
      notify(translate('resources.equipment-fuel.notifications.deleted'), { type: 'success' });
      refresh();
      onClose();
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Delete error:', error);
      notify(translate('resources.equipment-fuel.notifications.delete_error'), { type: 'error' });
    } finally {
      false;
    }
  };

  return (
    <>
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm">
        <DialogTitle>{translate('resources.equipment_fuel.delete.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>{translate('resources.equipment_fuel.delete.content')}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {translate('ra.action.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {translate('ra.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          {isEdit
            ? translate('resources.equipment_fuel.edit.title')
            : translate('resources.equipment_fuel.create.title')}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SimpleForm
            onSubmit={handleSubmit}
            record={
              record || {
                fuelDate: new Date().toISOString().split('T')[0],
              }
            }
            toolbar={<FuelToolbar isEdit={isEdit} onDelete={() => setDeleteConfirmOpen(true)} />}
          >
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DateInput source="fuelDate" validate={[required()]} fullWidth label="Fuel Date" />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="fuelType"
                  choices={fuelTypes}
                  validate={[required()]}
                  fullWidth
                  label="Fuel Type"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput source="quantity" validate={[required()]} fullWidth label="Quantity" />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  source="unitCost"
                  validate={[required()]}
                  fullWidth
                  label="Unit Cost ($)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  source="totalCost"
                  validate={[required()]}
                  fullWidth
                  label="Total Cost ($)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumberInput source="mileage" fullWidth label="Mileage" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput source="location" fullWidth label="Location" />
              </Grid>
            </Grid>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};
